import { useRef, useState, type ChangeEvent } from "react";
import { App, Upload } from "antd";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import type { User as AuthUser } from "firebase/auth";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import type { AnalyzedItem, AnalyzeImageResponse } from "@gull/shared";
import { storage } from "../../../lib/firebase";
import { enduserApi, ApiError } from "../../../lib/api";
import { isCameraCaptureSupported } from "../../../components/CameraCapture";
import { ALLOWED_IMG_TYPES, MAX_BYTES, MAX_ORDER_IMAGES } from "../constants";

/** `suggestions` earns the AI treatment in the UI; every other outcome is a plain note. */
export interface ImageNotice {
  tone: "suggestions" | "info";
  text: string;
}

export function useOrderImages({
  user,
  onItemsSuggested,
}: {
  user: AuthUser | null;
  onItemsSuggested: (items: AnalyzedItem[]) => void;
}) {
  const { message } = App.useApp();
  const [paths, setPaths] = useState<string[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzedPaths, setAnalyzedPaths] = useState<string[]>([]);
  const [notice, setNotice] = useState<ImageNotice | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const uploadedUids = useRef<Set<string>>(new Set());
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const validateImageFile = (file: File) => {
    if (!ALLOWED_IMG_TYPES.includes(file.type)) {
      message.error("Bare JPEG, PNG eller WEBP");
      return false;
    }
    if (file.size > MAX_BYTES) {
      message.error("Bilde er større enn 8 MB");
      return false;
    }
    return true;
  };

  const beforeUpload = (file: File) => (validateImageFile(file) ? false : Upload.LIST_IGNORE);

  const analyze = async (pathsToConsider: string[]) => {
    const pending = pathsToConsider.filter((path) => !analyzedPaths.includes(path));
    if (pending.length === 0) {
      setNotice({
        tone: "info",
        text: "Bildene er allerede analysert. Du kan endre listen manuelt.",
      });
      return;
    }
    setAnalyzing(true);
    setNotice(null);
    let imagesWithItems = 0;
    try {
      for (const path of pending) {
        const result = await enduserApi.authedPost<AnalyzeImageResponse>(
          "/mail-orders/images/analyze",
          { storagePath: path },
        );
        if (result.items.length > 0) {
          imagesWithItems += 1;
          onItemsSuggested(result.items);
        }
        setAnalyzedPaths((prev) => (prev.includes(path) ? prev : [...prev, path]));
      }
      setNotice(
        imagesWithItems > 0
          ? {
              tone: "suggestions",
              text: "Vi har funnet forslag til gjenstander og har lagt dem til i listen. Se over, og juster beskrivelse hvis noe ikke stemmer.",
            }
          : {
              tone: "info",
              text: "Vi kjente ikke igjen noen gjenstander i bildet. Legg dem inn manuelt under.",
            },
      );
    } catch (err) {
      setNotice({
        tone: "info",
        text:
          err instanceof ApiError && err.code === "AI_NOT_CONFIGURED"
            ? "AI-analyse er ikke tilgjengelig akkurat nå. Legg inn gjenstandene manuelt under."
            : "Vi kunne ikke analysere bildet akkurat nå. Legg inn gjenstandene manuelt under.",
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const upload = async (files: UploadFile[]) => {
    if (!user) return;
    const toUpload = files.filter(
      (file) => file.originFileObj && !uploadedUids.current.has(file.uid),
    );
    if (toUpload.length === 0) return;
    setUploading(true);
    try {
      const newPaths: string[] = [];
      for (const wrapped of toUpload) {
        uploadedUids.current.add(wrapped.uid);
        const file = wrapped.originFileObj as File;
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
        const path = `mail-order-uploads/${user.uid}/${Date.now()}-${crypto.randomUUID()}.${ext}`;
        try {
          await uploadBytes(storageRef(storage, path), file, { contentType: file.type });
          newPaths.push(path);
        } catch (err) {
          uploadedUids.current.delete(wrapped.uid);
          throw err;
        }
      }
      setPaths((prev) => [...prev, ...newPaths]);
      setFileList([]);
      await analyze(newPaths);
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Bildet kunne ikke lastes opp");
    } finally {
      setUploading(false);
    }
  };

  const uploadCapturedFile = async (file: File) => {
    if (!validateImageFile(file)) return;
    const uid = crypto.randomUUID();
    const originFileObj = Object.assign(file, { uid }) as RcFile;
    await upload([{ uid, name: file.name, originFileObj }]);
  };

  const handleCameraInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) await uploadCapturedFile(file);
  };

  const openCamera = () => {
    if (isCameraCaptureSupported()) {
      setCameraOpen(true);
      return;
    }
    cameraInputRef.current?.click();
  };

  // Every "info" notice tells the customer to add items manually; adding one answers it.
  const dismissManualHint = () =>
    setNotice((current) => (current?.tone === "info" ? null : current));

  const remove = (path: string) => {
    const remaining = paths.filter((p) => p !== path);
    setPaths(remaining);
    setAnalyzedPaths((prev) => prev.filter((p) => p !== path));
    setFileList((prev) => prev.filter((f) => !path.endsWith(f.name)));
    if (remaining.length === 0) setNotice(null);
  };

  return {
    paths,
    fileList,
    setFileList,
    uploading,
    analyzing,
    notice,
    busy: uploading || analyzing,
    atLimit: paths.length >= MAX_ORDER_IMAGES,
    remainingSlots: MAX_ORDER_IMAGES - paths.length,
    beforeUpload,
    upload,
    uploadCapturedFile,
    handleCameraInputChange,
    cameraInputRef,
    cameraOpen,
    openCamera,
    dismissManualHint,
    closeCamera: () => setCameraOpen(false),
    remove,
    restore: setPaths,
  };
}

export type OrderImages = ReturnType<typeof useOrderImages>;
